import { formatCurrency } from '@/hooks/purchase/useFormattedCurrency';
import { Subscription } from '@/store/user/types';
import { STRIPE_ZERO_DECIMAL_CURRENCIES } from '@/utils/prices/constants';

import { getPromoAttribution } from './getPromoAttribution';

type SearchParamsLike = { get: (name: string) => string | null } | null | undefined;

export const UPSELL_PURCHASE_COMPLETED = 'Upsell : Purchase : Completed';

// Field names and formats mirror the mobile Upsell : Product : * events: prices are plain
// numbers in major units and localized_price is the display string.
const getChargedPriceProps = (
	subscription: Subscription | null | undefined,
	browserLanguage: string | null,
) => {
	const charge = subscription?.first_invoice;
	if (!charge) {
		return {};
	}
	const currency = charge.currency.toUpperCase();
	const isZeroDecimal = STRIPE_ZERO_DECIMAL_CURRENCIES.has(charge.currency.toLowerCase());
	const price = isZeroDecimal ? charge.amount : charge.amount / 100;
	const isUsd = currency === 'USD';
	const renewalAmount = subscription?.stripe_details?.original_amount;
	return {
		price_in_currency: price,
		localized_currency: currency,
		// formatCurrency takes the store's cents convention, which is 100x even for zero-decimal currencies.
		localized_price: formatCurrency(
			isZeroDecimal ? charge.amount * 100 : charge.amount,
			currency,
			browserLanguage,
		),
		// No FX rate is available on web or in the api, so a USD amount is only reported for a USD charge.
		...(isUsd ? { price_in_usd: price } : {}),
		...(isUsd && typeof renewalAmount === 'number' ? { renewal_price_in_usd: renewalAmount / 100 } : {}),
	};
};

/** A price in the store's cents convention, as the checkout page showed it. */
export type ShownPrice = { amount: number; currency: string };

// Used when no charge is available. Like the mobile Stripe path, it reports the shown price, not a charge.
const getShownPriceProps = (shownPrice: ShownPrice | null | undefined, browserLanguage: string | null) => {
	if (!shownPrice || shownPrice.amount <= 0) {
		return {};
	}
	const currency = shownPrice.currency.toUpperCase();
	const price = shownPrice.amount / 100;
	return {
		price_in_currency: price,
		localized_currency: currency,
		localized_price: formatCurrency(shownPrice.amount, currency, browserLanguage),
		...(currency === 'USD' ? { price_in_usd: price } : {}),
	};
};

/**
 * Props for the experiment conversion event, or null when the purchase did not come from a
 * seasonal offer link (the link's flavor is what marks it).
 */
export const createUpsellPurchaseCompletedProps = ({
	searchParams,
	subscription,
	plan,
	browserLanguage,
	shownPrice,
}: {
	searchParams: SearchParamsLike;
	subscription: Subscription | null | undefined;
	plan: string | null | undefined;
	browserLanguage: string | null;
	shownPrice?: ShownPrice | null;
}) => {
	const attribution = getPromoAttribution(searchParams);
	if (!attribution.flavor) {
		return null;
	}
	return {
		flavor: attribution.flavor,
		variant: attribution.variant ?? null,
		pack_class: attribution.pack_class ?? null,
		coupon: searchParams?.get('coupon') ?? subscription?.coupon_used ?? null,
		plan: plan ?? null,
		...(subscription?.first_invoice
			? getChargedPriceProps(subscription, browserLanguage)
			: getShownPriceProps(shownPrice, browserLanguage)),
	};
};
